import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, AlertTitle, Box, Button, Chip, Drawer, IconButton, Stack, Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '@/lib/auth/AuthContext';
import { useAnalyticsApi } from '@/lib/useAnalytics';
import { LogowanieRestauracji } from './LogowanieRestauracji';
import { POZYCJE_PANELU, sciezkaPozycji } from './nawigacja';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * POWŁOKA PANELU RESTAURACJI — ODDZIELNY PRODUKT, NIE WIDOK CONTROL CENTER
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ CO ZGŁOSIŁ WŁAŚCICIEL I CO Z TEGO WYNIKA ─────────────────────────────────────────────────┐
 * │ Na zrzucie ekranu „panel restauracji" pokazywał Administrację, CRM, przełącznik paneli      │
 * │ i podgląd restauracji. To był PODGLĄD WŁAŚCICIELA i w tej roli jest w porządku — właściciel │
 * │ pozostaje właścicielem, także gdy ogląda cudze dane.                                        │
 * │                                                                                              │
 * │ Niedopuszczalne byłoby pokazanie tego samego RESTAURACJI. Dlatego panel najemcy ma własną   │
 * │ powłokę: własne logowanie, własną nawigację i żadnego elementu konsoli.                     │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ DWA TRYBY, KTÓRYCH NIE WOLNO ZLAĆ ─────────────────────────────────────────────────────────┐
 * │ OWNER_RESTAURANT_PREVIEW  — właściciel w konsoli, `#/panel-restauracji`, zakres z sesji      │
 * │                             podglądu ustanowionej po potwierdzeniu hasłem.                   │
 * │ REAL_RESTAURANT_SESSION   — restauracja, `#/restauracja`, zakres z WŁASNEJ tożsamości.       │
 * │                                                                                              │
 * │ Wspólne są komponenty ekranów i usługi danych. RÓŻNY jest kontekst autoryzacji — i dlatego   │
 * │ ten plik nigdy nie wpuszcza konta administracyjnego do trybu najemcy „na skróty".            │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 */

const SZEROKOSC_MENU = 248;

/** Czy to konto jest kontem restauracji. Rola jest jedynym źródłem tej odpowiedzi. */
function czyKontoRestauracji(role: readonly string[] | undefined): boolean {
  return (role ?? []).includes('restaurant');
}

function Nawigacja({ onWybor }: { onWybor?: () => void }) {
  const location = useLocation();
  return (
    <Stack component="nav" aria-label="Panel restauracji" spacing={0.25} sx={{ p: 1.5 }}>
      {POZYCJE_PANELU.map((p) => {
        const sciezka = sciezkaPozycji(p);
        const aktywna = location.pathname === sciezka;
        return (
          <Box
            key={p.id}
            component={NavLink}
            to={sciezka}
            end={p.sciezka === ''}
            onClick={onWybor}
            aria-current={aktywna ? 'page' : undefined}
            sx={{
              display: 'block',
              px: 1.5, py: 1,
              borderRadius: 'var(--r-maly, 8px)',
              textDecoration: 'none',
              color: aktywna ? 'var(--atrament)' : 'var(--tekst-na-ciemnym)',
              background: aktywna ? 'var(--zloto)' : 'transparent',
              fontWeight: aktywna ? 800 : 600,
              '&:hover': { background: aktywna ? 'var(--zloto)' : 'var(--atrament-3)' },
            }}
          >
            <Typography component="span" sx={{ display: 'block', fontSize: 14, fontWeight: 'inherit' }}>
              {p.etykieta}
            </Typography>
            <Typography
              component="span"
              sx={{
                display: 'block', fontSize: 11, lineHeight: 1.3,
                color: aktywna ? 'var(--atrament-3)' : 'var(--tekst-slaby)',
              }}
            >
              {p.opis}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
}

/**
 * Ekran dla konta o roli restauracji, które nie ma jeszcze przypisanej restauracji.
 *
 * ┌─ MÓWIMY, CO SIĘ DZIEJE, ZAMIAST POKAZAĆ PUSTE TABELE ──────────────────────────────────────┐
 * │ Puste ekrany czyta się jako „nic dziś nie sprzedaliśmy", a komunikat błędu jako awarię.    │
 * │ Ani jedno, ani drugie nie jest prawdą: konto po prostu nie jest jeszcze związane           │
 * │ z restauracją. Nazwanie tego wprost jest jedyną wersją, po której wiadomo, co robić.        │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 */
function OczekiwanieNaWiazanie({ konto }: { konto: string }) {
  return (
    <Box sx={{ p: 3, maxWidth: 680, mx: 'auto' }}>
      <Alert severity="info">
        <AlertTitle>Konto nie jest jeszcze przypisane do restauracji</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Logowanie zadziałało — konto <strong>{konto}</strong> istnieje i ma rolę restauracji.
          Brakuje ostatniego kroku: powiązania go z konkretną restauracją.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Do czasu tego powiązania panel nie pokazuje żadnych danych. Nie jest to awaria i nie
          znaczy, że danych nie ma — serwer odmawia, zamiast zgadywać, czyje miałby pokazać.
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
          WAITING_FOR_RESTAURANT_ACCOUNT_BINDING
        </Typography>
      </Alert>
    </Box>
  );
}

/*
 * ┌─ CO ZNIKŁO STĄD 2026-08-27 I DLACZEGO ─────────────────────────────────────────────────────┐
 * │ Stał tu ekran „to jest panel restauracji, wróć do Control Center" pokazywany kontu         │
 * │ administracyjnemu. Był poprawny wobec ówczesnego założenia — że właściciel wchodzi w cudze │
 * │ dane WYŁĄCZNIE przez podgląd w konsoli.                                                     │
 * │                                                                                              │
 * │ Właściciel rozstrzygnął inaczej: ma móc zalogować się TUTAJ, wpisując login restauracji     │
 * │ i własne hasło. Odsyłanie go do konsoli po to, żeby wrócił w to samo miejsce, było krokiem  │
 * │ nic nie chroniącym.                                                                          │
 * │                                                                                              │
 * │ Rozdzielenie trybów zostaje nienaruszone: autorytet właściciela nadal wymaga JEGO hasła,   │
 * │ zakres nadal ustala serwer, a sesja mistrza jest tylko do odczytu.                          │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 */

/**
 * Stan sesji podglądu — CZYJE dane są otwarte.
 *
 * Pytamy serwer, bo ciasteczko zakresu jest httpOnly i skrypt strony go nie czyta. To nie jest
 * niedogodność, tylko sedno: gdyby front sam trzymał identyfikator najemcy, byłby to zakres
 * wybierany w przeglądarce.
 */
function useStanPodgladu(wlaczone: boolean) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['podglad-stan'],
    queryFn: () => api.stanPodgladu(),
    enabled: wlaczone,
    staleTime: 60_000,
    retry: false,
  });
}

export function PowlokaRestauracji() {
  const auth = useAuth();
  const kolejka = useQueryClient();
  const api = useAnalyticsApi();
  const [menuOtwarte, setMenuOtwarte] = useState(false);
  const tozsamosc = auth.realms.cc?.identity ?? null;

  const zalogowany = auth.status === 'authenticated' && tozsamosc !== null;
  const najemca = zalogowany && czyKontoRestauracji(tozsamosc.roles);
  const wlasciciel = zalogowany && !najemca;

  /*
   * Stan podglądu pytamy TYLKO dla konta właściciela. Konto najemcy nie ma dostępu do tej
   * trasy (granica najemcy odpowie 403) i pytanie o nią byłoby zapowiedzią odmowy.
   */
  const podglad = useStanPodgladu(wlasciciel);

  async function wyloguj() {
    await auth.signOut();
    await kolejka.invalidateQueries();
  }

  async function zakonczPodglad() {
    await api.zakonczPodglad();
    await kolejka.invalidateQueries();
  }

  // Dopóki nie wiadomo, czy sesja istnieje, nie pokazujemy ani panelu, ani logowania —
  // migający formularz przy odświeżeniu strony wygląda jak wylogowanie.
  if (auth.status === 'unknown') {
    return <Box sx={{ p: 4 }}><Typography variant="body2">Sprawdzanie sesji…</Typography></Box>;
  }

  /*
   * ┌─ PO ZALOGOWANIU TRZEBA ODCZYTAĆ SESJĘ, A NIE ODŚWIEŻYĆ ZAPYTANIA ─────────────────────────┐
   * │ Poprzednia wersja wołała tu wyłącznie `invalidateQueries()`. To dotyczy pamięci          │
   * │ podręcznej zapytań — a o tym, czy panel się w ogóle otworzy, decyduje STAN               │
   * │ UWIERZYTELNIENIA, który żył wtedy tylko w efekcie montowania.                             │
   * │                                                                                           │
   * │ Skutek był dokładnie taki, jak zgłosił właściciel: serwer ustawiał ciasteczko i          │
   * │ odpowiadał 200, a ekran renderował formularz logowania jeszcze raz. Logowanie DZIAŁAŁO,  │
   * │ tylko aplikacja nigdy się o tym nie dowiadywała.                                          │
   * │                                                                                           │
   * │ Kolejność ma znaczenie: NAJPIERW sesja, POTEM dane. Odwrotnie zapytania poleciałyby       │
   * │ jeszcze bez zakresu i wróciły z 401.                                                      │
   * └───────────────────────────────────────────────────────────────────────────────────────────┘
   */
  const odswiez = async () => {
    await auth.odswiezSesje();
    await kolejka.invalidateQueries();
  };

  if (!zalogowany) return <LogowanieRestauracji onZalogowano={() => { void odswiez(); }} />;

  const konto = tozsamosc.subject;
  const restauracjaPodgladu = podglad.data?.aktywny === true ? podglad.data : null;

  /*
   * ┌─ TRZY DROGI DO TEGO PANELU ────────────────────────────────────────────────────────────────┐
   * │ 1. REAL_RESTAURANT_SESSION  — konto restauracji z wiązaniem (R5-14). Zakres z tożsamości. │
   * │ 2. OWNER_RESTAURANT_MASTER  — właściciel, który wskazał restaurację jej loginem i         │
   * │                               potwierdził SWOIM hasłem. Zakres z sesji podglądu.          │
   * │ 3. właściciel BEZ wskazanej restauracji — nie wiadomo, czyje dane pokazać, więc wraca      │
   * │    formularz. Nie „NieTenPanel": właściciel MA tu prawo wejść, tylko musi wskazać cel.    │
   * │                                                                                             │
   * │ Autorytet jest w każdym z nich inny i tak ma zostać. Wspólna jest wyłącznie POWŁOKA —      │
   * │ bo pytanie „co widzi restauracja" ma jedną odpowiedź, niezależnie od tego, kto patrzy.    │
   * └─────────────────────────────────────────────────────────────────────────────────────────────┘
   */
  const zwiazane = najemca && tozsamosc.restauracjaId !== null;
  const wTrybieMistrza = wlasciciel && restauracjaPodgladu !== null;

  if (wlasciciel && podglad.isLoading) {
    return <Box sx={{ p: 4 }}><Typography variant="body2">Sprawdzanie kontekstu…</Typography></Box>;
  }
  if (wlasciciel && !wTrybieMistrza) {
    return <LogowanieRestauracji onZalogowano={() => { void odswiez(); }} />;
  }

  const nazwaRestauracji = wTrybieMistrza
    ? (restauracjaPodgladu.nazwa ?? `nr ${restauracjaPodgladu.restauracja_id}`)
    : null;

  const naglowek = (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        alignItems: 'center', px: 2, py: 1.25,
        borderBottom: '1px solid var(--linia)',
        background: 'var(--powierzchnia)',
        position: 'sticky', top: 0, zIndex: 2,
        flexWrap: 'wrap', rowGap: 0.5,
      }}
    >
      <IconButton
        aria-label="Menu panelu"
        onClick={() => setMenuOtwarte(true)}
        sx={{ display: { md: 'none' } }}
        size="small"
      >
        <MenuIcon />
      </IconButton>

      <StorefrontIcon fontSize="small" sx={{ color: 'var(--zloto-glebokie)' }} />
      <Typography component="span" sx={{ fontWeight: 800, fontSize: 15, minWidth: 0 }}>
        Panel restauracji
      </Typography>

      {/*
        KONTEKST WŁAŚCICIELA JEST STAŁY I NIE DA SIĘ GO PRZEOCZYĆ.
        Właściciel nigdy nie może się zastanawiać, czyje dane właśnie ogląda — pomyłka tutaj
        to decyzja podjęta na cudzych liczbach. Nie ma tu za to ANI JEDNEGO elementu konsoli.
      */}
      {wTrybieMistrza && (
        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            alignItems: 'center', px: 1, py: 0.25, borderRadius: 1,
            background: 'var(--uwaga-tlo)', border: '1px solid var(--uwaga)',
          }}
        >
          <VisibilityIcon fontSize="small" sx={{ color: 'var(--uwaga)' }} />
          <Typography variant="caption" sx={{ color: 'var(--uwaga)', fontWeight: 700 }}>
            Podgląd właściciela
          </Typography>
          <Chip size="small" label={nazwaRestauracji} />
          <Chip size="small" variant="outlined" label="tylko odczyt" />
        </Stack>
      )}

      <Box sx={{ flexGrow: 1 }} />

      <Typography
        variant="body2"
        sx={{
          color: 'var(--tekst-slaby)', minWidth: 0, maxWidth: 260,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          display: { xs: 'none', sm: 'block' },
        }}
      >
        {konto}
      </Typography>

      {wTrybieMistrza && (
        // Powrót do formularza wskazania restauracji — bez wylogowywania właściciela.
        <Button size="small" onClick={() => { void zakonczPodglad(); }}>Zmień restaurację</Button>
      )}

      <Button
        size="small"
        color="inherit"
        startIcon={<LogoutIcon />}
        onClick={() => { void wyloguj(); }}
      >
        Wyloguj
      </Button>
    </Stack>
  );

  if (najemca && !zwiazane) {
    return (
      <Box sx={{ minHeight: '100vh', background: 'var(--tlo)' }}>
        {naglowek}
        <OczekiwanieNaWiazanie konto={konto} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: 'var(--tlo)' }}>
      {/* Menu stałe od `md` w górę; niżej wysuwane, bo restauracja czyta te ekrany na tablecie. */}
      <Box
        component="aside"
        sx={{
          width: SZEROKOSC_MENU, flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          background: 'var(--atrament)',
          color: 'var(--tekst-na-ciemnym)',
        }}
      >
        <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid var(--linia-na-ciemnym)' }}>
          <Typography sx={{ fontWeight: 800, fontSize: 16 }}>TuSzama</Typography>
          <Typography sx={{ fontSize: 12, color: 'var(--tekst-slaby)' }}>Panel restauracji</Typography>
        </Box>
        <Nawigacja />
      </Box>

      <Drawer open={menuOtwarte} onClose={() => setMenuOtwarte(false)}>
        <Box sx={{ width: SZEROKOSC_MENU, background: 'var(--atrament)', minHeight: '100%' }}>
          <Nawigacja onWybor={() => setMenuOtwarte(false)} />
        </Box>
      </Drawer>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {naglowek}
        <Box component="main" sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

export default PowlokaRestauracji;
